import { ConnectionDisplayName } from '@app/modules/social-export/components/ConnectionDisplayName';
import { SocialPlatform } from '@app/modules/social-export/domain/platforms';
import { SocialExportGetStationConnectionStateDto } from '@mi-front/api-v2';
import {
  Avatar,
  FormControl,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  SxProps,
  Theme,
} from '@mui/material';
import { ReactNode } from 'react';

type ConnectionSelectProps = {
  connections: SocialExportGetStationConnectionStateDto[];
  value: string | null;
  onChange: (id: string | null) => void;
  platform: SocialPlatform;
  disabled?: boolean;
  /**
   * Keeps enabled styling (full-opacity text/avatar) while preventing interaction
   * and hiding the dropdown caret. Prefer over `disabled` when the value is fixed
   * (e.g. only one connection available).
   */
  readOnly?: boolean;
  size?: 'small' | 'medium';
  /** When set, renders an InputLabel on the Select. */
  label?: ReactNode;
  id?: string;
  sx?: SxProps<Theme>;
};

/**
 * Select to pick a social-export connection, showing profile photo + display name.
 */
export function ConnectionSelect({
  connections,
  value,
  onChange,
  platform,
  disabled = false,
  readOnly = false,
  size = 'medium',
  label,
  id = 'connection-select',
  sx,
}: ConnectionSelectProps) {
  const selectedConnection = connections.find((c) => c.id === value) ?? null;
  const photo = selectedConnection?.profile.photo;
  const labelId = label ? `${id}-label` : undefined;
  const selectedAvatarSize = size === 'small' ? 22 : 32;
  const menuAvatarSize = size === 'small' ? 22 : 28;

  /** Softer than MUI's default grey.400 so the empty-photo circle doesn't dominate either theme. */
  const avatarSx = (avatarSize: number) => ({
    width: avatarSize,
    height: avatarSize,
    bgcolor: (theme: Theme) => (theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200'),
  });

  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value || null);
  };

  return (
    <FormControl fullWidth size={size} sx={sx}>
      {label ? <InputLabel id={labelId}>{label}</InputLabel> : null}
      <Select
        id={id}
        labelId={labelId}
        label={label}
        value={value ?? ''}
        onChange={handleChange}
        disabled={disabled}
        readOnly={readOnly}
        IconComponent={readOnly ? () => null : undefined}
        displayEmpty={!label}
        sx={readOnly ? { pointerEvents: 'none' } : undefined}
        startAdornment={
          selectedConnection ? (
            <InputAdornment position="start">
              <Avatar src={photo || undefined} sx={avatarSx(selectedAvatarSize)}>
                {!photo ? platform.Icon({ colored: true }) : null}
              </Avatar>
            </InputAdornment>
          ) : undefined
        }
        renderValue={(selectedId) => {
          const connection = connections.find((c) => c.id === selectedId);
          return connection ? (
            <ConnectionDisplayName connection={connection} component="span" />
          ) : (
            ''
          );
        }}
      >
        {connections.map((connection) => (
          <MenuItem key={connection.id} value={connection.id}>
            <ListItemIcon sx={{ minWidth: size === 'small' ? 36 : 40 }}>
              <Avatar
                src={connection.profile.photo || undefined}
                sx={avatarSx(menuAvatarSize)}
              >
                {!connection.profile.photo ? platform.Icon({ colored: true }) : null}
              </Avatar>
            </ListItemIcon>
            <ListItemText
              disableTypography
              primary={<ConnectionDisplayName connection={connection} component="span" />}
            />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
